<template>
  <section v-loading.fullscreen="isLoading" class="game-display-page">
    <Teleport v-if="isMounted" to="#teleport-app-header-content">
      <div v-if="game" class="header-content">
        <GameVersionSelector :game="game" :current-version-id="versionId" :versions="versions" />
        <div v-if="canWriteVersions">
          <vd-button
            v-if="inWIPVersion"
            class="upload"
            type="primary"
            size="large"
            :disabled="publicationInProgress"
            @click="onPublish"
          >
            <vd-custom-icon name="publish-icon" class="publish-icon" color="var(--vd-color-white)" />
            <span>Publish</span>
          </vd-button>
          <vd-button
            v-if="inLiveVersion"
            class="upload"
            type="primary"
            :disabled="publicationInProgress"
            @click="useVersionAsWIP"
          >
            <vd-custom-icon name="traffic-plot-icon" class="traffic-plot-icon" color="var(--vd-color-white)" />
            <span>Use as WIP version</span>
          </vd-button>
          <vd-dropdown v-if="!errorState && !inWIPVersion && !inLiveVersion">
            <template #head>
              <vd-button class="upload" type="tertiary" icon-right="fa:chevron-down">
                <vd-custom-icon name="publish-icon" class="publish-icon" color="var(--vd-base-primary-color)" />
                <span>Reuse this version</span>
              </vd-button>
            </template>
            <vd-dropdown-item @click="useVersionAsWIP">
              <vd-custom-icon name="traffic-plot-icon" class="traffic-plot-icon" color="var(--vd-base-primary-color)" />
              <span>as WIP version</span>
            </vd-dropdown-item>
            <vd-dropdown-item @click="rollbackToVersion">
              <vd-custom-icon name="live-icon" class="live-icon" />
              <span>as Live version (publish it)</span>
            </vd-dropdown-item>
          </vd-dropdown>
        </div>
      </div>
    </Teleport>
    <section v-if="!isMobileOrTabletScreen" class="menu">
      <GameMenu v-if="!errorState" class="sticky" :disable-test-act-section="disableTestActSection" />
    </section>
    <section class="content">
      <vd-error
        v-if="errorState"
        :status="errorState.status"
        :message="errorState.message"
        :back-link="errorState.backLink"
      />
      <div v-else-if="currentVersion">
        <el-alert
          v-if="publicationInProgress"
          :title="publicationBannerTitle"
          type="warning"
          :closable="false"
          center
          class="old-version-warning"
        />
        <el-alert
          v-if="inLiveVersion"
          title="You are viewing the live version."
          type="success"
          :closable="false"
          center
          class="old-version-warning"
        />
        <el-alert
          v-else-if="!inWIPVersion"
          title="You are viewing an old version."
          type="warning"
          :closable="false"
          center
          class="old-version-warning"
        />
        <router-view />
      </div>
    </section>
  </section>
</template>

<script lang="ts">
/**
 * This component is a layout that will host the different sub-pages like (classes, configuration, abtests...)
 * Its responsibility is to display the menu on the right and override the AppHeader with meaningful information.
 */
import { computed, defineComponent, onMounted, provide, ref } from "vue";
import { useRouter } from "vue-router";

import { PROVIDED_VALUES } from "@/constants";
import {
  GameDisplayPageDocument,
  useGameDisplayPageQuery,
  useGameDisplayPageResetVersionMutation,
  useGameDisplayPageRollbackVersionMutation,
  useMutationResponse,
} from "@/graphql";
import useMessages from "@/hooks/messages";
import useScreenSizes from "@/hooks/screen-sizes";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import usePermissions from "@/hooks/use-permissions";
import usePublicationStatus from "@/hooks/usePublicationStatus";
import links from "@/router/links";

import GameMenu from "./components/GameMenu.vue";
import GameVersionSelector from "./components/GameVersionSelector.vue";

export default defineComponent({
  name: "GameDisplayPage",

  components: {
    GameMenu,
    GameVersionSelector,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
    versionId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const router = useRouter();
    const { canWriteVersions } = usePermissions();
    const { flags } = useFeatureFlags(props.gameId);
    const { confirm } = useMessages();

    const { result, loading } = useGameDisplayPageQuery(() => ({
      gameId: props.gameId,
      versionId: props.versionId,
    }));

    const game = computed(() => result.value?.game);
    const gameNotFound = computed(() => !loading.value && !game.value);

    const bundleId = computed(() => result.value?.game?.bundleId);

    const versions = computed(() => result.value?.game?.versions || []);

    const currentVersion = computed(() => versions.value.find((v) => v.humanId === props.versionId));

    const inLiveVersion = computed(() => currentVersion.value?.isLive || false);
    const inWIPVersion = computed(() => currentVersion.value?.isWip || false);

    const versionNotFound = computed(() => !loading.value && !currentVersion.value);

    const disableTestActSection = computed(() => (game.value?.version?.classes.length ?? 0) === 0);

    const { isMobileOrTabletScreen } = useScreenSizes();

    const { publicationInProgress, startedBy, startedAt } = usePublicationStatus(props.gameId);

    const publicationBannerTitle = computed(() => {
      const by = startedBy.value ? ` by ${startedBy.value}` : "";
      const at = startedAt.value ? ` at ${new Date(startedAt.value).toLocaleTimeString()}` : "";
      return `Publication in progress, started${by}${at}. Editing is temporarily disabled.`;
    });

    provide(PROVIDED_VALUES.GAME.BUNDLE_ID, bundleId); // DEPRECATED

    /** provide inWIPVersion to all the children pages */
    provide(PROVIDED_VALUES.IN_WIP_VERSION, inWIPVersion); // DEPRECATED

    /** the error object that will contains all the needed info */
    const errorState = computed(() => {
      if (gameNotFound.value) {
        return {
          status: "404",
          message: "The game you are looking for does not exists",
          backLink: { name: links.games.list },
        };
      }

      if (versionNotFound.value) {
        return {
          status: "404",
          message: "The game version you are looking for does not exists",
          backLink: { name: links.games.home, params: { id: props.gameId, versionId: "wip" } },
        };
      }

      return null;
    });

    /**
     * Adds the ability to reset the actual version as the current WIP version
     */
    const { mutate: mutateVersionToWIP, loading: resetLoading } = useGameDisplayPageResetVersionMutation({
      refetchQueries: [
        { query: GameDisplayPageDocument, variables: { gameId: props.gameId, versionId: props.versionId } },
      ],
    });

    const useVersionAsWIP = async () => {
      if (publicationInProgress.value) {
        return;
      }
      const confirmed = await confirm(
        `This rollback operation will erase all the changes done on the current WIP version, replacing it by the target version. Are you sure you want to continue?`,
        { type: "warning" }
      );
      if (confirmed) {
        const response = await mutateVersionToWIP({
          gameId: props.gameId,
          versionId: props.versionId,
        });
        const data = useMutationResponse(response, (resp) => resp?.resetVersion);
        if (data?.success) {
          router.push({ name: links.games.home, params: { gameId: props.gameId, versionId: "wip" } });
        }
      }
    };

    /**
     * Adds the ability to use the current Version as the new Live version
     */
    const { mutate: mutateRollbackToVersion, loading: rollbackLoading } = useGameDisplayPageRollbackVersionMutation({
      refetchQueries: [
        { query: GameDisplayPageDocument, variables: { gameId: props.gameId, versionId: props.versionId } },
      ],
    });

    const rollbackToVersion = async () => {
      if (publicationInProgress.value) {
        return;
      }
      const confirmed = await confirm(
        `All the A/B Tests of the selected version will now become mutable. Are you sure you want to continue?`,
        { type: "warning" }
      );
      if (confirmed) {
        const response = await mutateRollbackToVersion({
          gameId: props.gameId,
          versionId: props.versionId,
        });

        const data = useMutationResponse(response, (resp) => resp?.rollbackVersion);

        if (data?.success) {
          const liveVersion = versions.value.find((version) => version.status === "live");
          if (liveVersion) {
            router.push({ name: links.games.home, params: { gameId: props.gameId, versionId: liveVersion.id } });
          }
        }
      }
    };

    const onPublish = () => {
      router.push({
        name: flags.value.partialPublish.enabled ? links.games.partialPublish : links.games.publish,
        params: { gameId: props.gameId },
      });
    };

    const isLoading = computed(() => loading.value || rollbackLoading.value || resetLoading.value);

    const isMounted = ref(false);
    onMounted(() => {
      isMounted.value = true;
    });

    return {
      isLoading,
      result,
      game,
      disableTestActSection,
      versions,
      currentVersion,
      isMobileOrTabletScreen,
      inWIPVersion,
      inLiveVersion,
      errorState,
      rollbackToVersion,
      useVersionAsWIP,
      canWriteVersions,
      isMounted,
      onPublish,
      publicationInProgress,
      publicationBannerTitle,
    };
  },
});
</script>

<style lang="scss">
.vd-error-component {
  margin-top: 20% !important;
}

.game-display-page {
  .header-content {
    .el-button--primary.is-plain:focus,
    .el-button--primary.is-plain:hover {
      .custom-icon-component {
        fill: $white;
      }
    }
  }

  .old-version-warning {
    margin-bottom: size("L");
  }

  .sticky {
    position: sticky;
    top: size("L");
  }
}
</style>

<style lang="scss" scoped>
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .publish-icon {
    margin-right: size("S");
  }

  @include medium-screen {
    .publish-icon {
      margin-right: 0;
    }

    .upload .el-button svg + span {
      display: none;
    }
  }
}

.game-display-page {
  display: grid;
  grid-template-columns: 195px calc(100% - 195px);
  grid-template-areas: "menu content";

  @include medium-screen {
    grid-template-columns: 100%;
    grid-template-areas: "content";
  }
}
</style>
